import { useState, useEffect, useCallback } from 'react';
import { translations, Language } from '../i18n/translations';

const STORAGE_KEY = 'totemium_landing_lang';

function getBrowserLanguage(): Language {
  try {
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'fr') return 'fr';
    if (browserLang === 'es') return 'es';
    return 'en';
  } catch {
    return 'en';
  }
}

function getInitialLanguage(preferredLanguage?: Language): Language {
  if (preferredLanguage) return preferredLanguage;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ['fr', 'en', 'es'].includes(stored)) {
      return stored as Language;
    }
  } catch {
    // Ignore storage errors.
  }

  return getBrowserLanguage();
}

type UseLanguageOptions = {
  preferredLanguage?: Language;
};

export function useLanguage({ preferredLanguage }: UseLanguageOptions = {}) {
  const [language, setLanguageState] = useState<Language>(() => getInitialLanguage(preferredLanguage));

  useEffect(() => {
    if (preferredLanguage && preferredLanguage !== language) {
      setLanguageState(preferredLanguage);
    }
  }, [preferredLanguage, language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch {
      // Ignore storage errors.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return {
    language,
    setLanguage,
    t: translations[language]
  };
}
